import { IpexMomentEnum } from '@insights/insights-data-provider';
import atMostOnce from 'utils/object-state/atMostOnce';
import notAfter from 'utils/object-state/notAfter';
import waitFor from 'utils/object-state/waitFor';
import { getQuery } from 'utils/queryUtils';
import withDefaults from 'utils/withDefaults';
import AnalyticsApi, { ConfigOptions } from './AnalyticsApi';
import AnalyticsEvent, { AnalyticsPayload } from './AnalyticsEvent';
import AnalyticsService from './AnalyticsService';
import EventReporter from './EventReporter';
import PayloadTransformer from './PayloadTransformer';
import { CoreApi, NoCore } from '@inter-ikea-storageone/range';

const dontSendEvents = getQuery().get('noAnalyticsEvents') === 'true';

const defaultConfigOptions: Required<ConfigOptions> = {
  payloadTransformers: [],
  coreApi: new NoCore({} as any, () => {})
};

async function* resolveAll(services: AnalyticsService[]) {
  for (const service of services) {
    yield service;
  }
}

export default class AnalyticsApiImpl implements AnalyticsApi {
  private payloadTransformers: PayloadTransformer[] = [];
  private services: AnalyticsService[] = [];
  private coreApi = {} as CoreApi;

  @atMostOnce
  @notAfter('connect', 'transformPayload')
  configure(options: ConfigOptions) {
    const optionsWithDefaults = withDefaults(options, defaultConfigOptions);
    this.payloadTransformers = optionsWithDefaults.payloadTransformers;
    this.coreApi = optionsWithDefaults.coreApi;
  }

  @atMostOnce
  async connect(...services: AnalyticsService[]) {
    if (dontSendEvents) {
      return;
    }
    this.services = services;
    for await (const service of resolveAll(services)) {
      await service.connect();
    }
  }

  reporter<TPayload extends AnalyticsPayload | void = void>(
    eventName: string,
    ipexMoment: IpexMomentEnum
  ): EventReporter<TPayload> {
    return ((payload: AnalyticsPayload) => {
      if (dontSendEvents) {
        return;
      }
      const transformedPayload = this.transformPayload(payload, ipexMoment);
      const event = { eventName, ipexMoment, payload: transformedPayload };
      this.report(event);
    }) as EventReporter<TPayload>;
  }

  transformPayload(payload: AnalyticsPayload, ipexMoment: IpexMomentEnum): AnalyticsPayload {
    return this.payloadTransformers.reduce(
      (payload, transformer) => transformer(payload, this.coreApi, ipexMoment),
      payload || {}
    );
  }

  @waitFor('connect')
  private report(event: AnalyticsEvent) {
    this.services.forEach(backend => backend.report(event));
  }
}
